<template>
  <teleport to="body">
    <transition name="hs-drawer">
      <div v-if="modelValue" class="hs-drawer" :style="{ zIndex }" role="dialog" aria-modal="true">
        <button class="hs-drawer__backdrop" type="button" @click="close" />
        <aside class="hs-drawer__panel" :class="[`hs-drawer__panel--${side}`, `hs-drawer__panel--${size}`]">
          <header class="hs-drawer__header">
            <div>
              <p v-if="kicker">{{ kicker }}</p>
              <h2>{{ title }}</h2>
            </div>
            <hs-button variant="secondary" icon="x" icon-only @click="close" />
          </header>
          <div class="hs-drawer__body">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="hs-drawer__footer">
            <slot name="footer" />
          </footer>
        </aside>
      </div>
    </transition>
  </teleport>
</template>

<script>
import HsButton from "./HsButton.vue";

export default {
  name: "HsDrawer",
  components: { HsButton },
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: "" },
    kicker: { type: String, default: "" },
    side: { type: String, default: "right", validator: (value) => ["left", "right"].includes(value) },
    size: { type: String, default: "md", validator: (value) => ["sm", "md", "lg"].includes(value) },
    zIndex: { type: [String, Number], default: 5100 },
  },
  emits: ["update:modelValue", "close"],
  watch: {
    modelValue(open) {
      document.body.style.overflow = open ? "hidden" : "auto";
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "auto";
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.hs-drawer {
  inset: 0;
  position: fixed;
}

.hs-drawer__backdrop {
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(8px);
  inset: 0;
  position: absolute;
}

.hs-drawer__panel {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 96%, transparent);
  border-color: var(--hs-border, rgba(148, 163, 184, 0.22));
  box-shadow: 0 28px 90px rgba(15, 23, 42, 0.24);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: absolute;
  top: 0;
  width: min(100%, 520px);
}

.hs-drawer__panel--sm { width: min(100%, 420px); }
.hs-drawer__panel--lg { width: min(100%, 720px); }
.hs-drawer__panel--right { border-left: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22)); right: 0; }
.hs-drawer__panel--left { border-right: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22)); left: 0; }

.hs-drawer__header,
.hs-drawer__footer {
  align-items: center;
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 1rem;
}

.hs-drawer__footer {
  border-bottom: 0;
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  justify-content: flex-end;
}

.hs-drawer__body {
  flex: 1;
  overflow: auto;
  padding: 1rem;
}

.hs-drawer p {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.12em;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.hs-drawer h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1.35rem;
  font-weight: 950;
  margin: 0;
}

.hs-drawer-enter-active,
.hs-drawer-leave-active {
  transition: opacity 0.18s ease;
}

.hs-drawer-enter-from,
.hs-drawer-leave-to {
  opacity: 0;
}
</style>
